/**
 * SettingsDialog - Application settings and backup/restore
 */
import React, { useState, useEffect } from 'react';
import { useProjectStore } from '../../stores';
import { Tema, nacitajTemu, nastavTemu } from '../../utils/tema';
import './Dialog.css';
import './SettingsDialog.css';

interface SettingsDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabType = 'general' | 'backup';

// Get API URL from environment
const API_URL = process.env.REACT_APP_API_URL && process.env.REACT_APP_API_URL !== '' ? process.env.REACT_APP_API_URL : '/api';

// Helper function to get auth token
const getAuthToken = (): string | null => {
  const authStorage = localStorage.getItem('auth-storage');
  if (authStorage) {
    try {
      const { state } = JSON.parse(authStorage);
      return state?.token || null;
    } catch (error) {
      console.error('Error parsing auth storage:', error);
      return null;
    }
  }
  return null;
};

export const SettingsDialog: React.FC<SettingsDialogProps> = ({ isOpen, onClose }) => {
  const { currentProject, updateProject } = useProjectStore();
  const [activeTab, setActiveTab] = useState<TabType>('general');
  const [tema, setTema] = useState<Tema>(nacitajTemu);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Hranice pre farebný prstenec signálu na mape. Držia sa v nastaveniach
  // projektu, nie v kóde — každá sieť má iné rezervy.
  const [prahZelena, setPrahZelena] = useState<string>('');
  const [prahOranzova, setPrahOranzova] = useState<string>('');
  const [ukladam, setUkladam] = useState(false);

  useEffect(() => {
    if (!currentProject) return;
    setPrahZelena(String(currentProject.settings?.signal_green_dbm ?? -15));
    setPrahOranzova(String(currentProject.settings?.signal_orange_dbm ?? -25));
  }, [currentProject]);

  const ulozPrahy = async () => {
    if (!currentProject) return;
    const z = parseFloat(prahZelena.replace(',', '.'));
    const o = parseFloat(prahOranzova.replace(',', '.'));
    if (!Number.isFinite(z) || !Number.isFinite(o)) {
      setMessage({ type: 'error', text: 'Hranice musia byť čísla, napríklad -15 a -25.' });
      return;
    }
    if (o >= z) {
      setMessage({ type: 'error', text: 'Oranžová hranica musí byť nižšia než zelená — signál slabne.' });
      return;
    }
    setUkladam(true);
    try {
      await updateProject(currentProject.id, {
        settings: { ...currentProject.settings, signal_green_dbm: z, signal_orange_dbm: o },
      } as any);
      setMessage({ type: 'success', text: 'Hranice signálu uložené.' });
    } catch (e: any) {
      setMessage({ type: 'error', text: 'Hranice sa nepodarilo uložiť.' });
    } finally {
      setUkladam(false);
    }
  };

  console.log('SettingsDialog render. isOpen:', isOpen);

  if (!isOpen) return null;

  // Export all projects as JSON
  const handleExportAllProjects = async () => {
    setExporting(true);
    setMessage(null);

    try {
      const token = getAuthToken();
      console.log('[Export] API_URL:', API_URL);
      console.log('[Export] Token:', token ? 'exists' : 'missing');
      console.log('[Export] Full URL:', `${API_URL}/export/all`);

      const response = await fetch(`${API_URL}/export/all`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      console.log('[Export] Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Export] Error response:', errorText);
        throw new Error(`Export failed: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      
      // Create download
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `gpon_backup_all_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setMessage({ type: 'success', text: 'Všetky projekty boli úspešne exportované!' });
    } catch (error) {
      console.error('Export error:', error);
      setMessage({ type: 'error', text: 'Export zlyhal. Skúste to znova.' });
    } finally {
      setExporting(false);
    }
  };

  // Export current project as JSON
  const handleExportCurrentProject = async () => {
    if (!currentProject) {
      setMessage({ type: 'error', text: 'Nie je vybraný projekt' });
      return;
    }

    setExporting(true);
    setMessage(null);

    try {
      const token = getAuthToken();
      console.log('[Export Project] API_URL:', API_URL);
      console.log('[Export Project] Token:', token ? 'exists' : 'missing');
      console.log('[Export Project] Project ID:', currentProject.id);
      console.log('[Export Project] Full URL:', `${API_URL}/export/project/${currentProject.id}`);

      const response = await fetch(`${API_URL}/export/project/${currentProject.id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      console.log('[Export Project] Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Export Project] Error response:', errorText);
        throw new Error(`Export failed: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      
      // Create download
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `gpon_backup_${currentProject.name}_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setMessage({ type: 'success', text: `Projekt "${currentProject.name}" bol úspešne exportovaný!` });
    } catch (error) {
      console.error('Export error:', error);
      setMessage({ type: 'error', text: 'Export zlyhal. Skúste to znova.' });
    } finally {
      setExporting(false);
    }
  };

  // Export database as SQL
  const handleExportSQL = async () => {
    setExporting(true);
    setMessage(null);

    try {
      const token = getAuthToken();
      const response = await fetch(`${API_URL}/export/sql`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('SQL export zlyhal');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `gpon_database_${new Date().toISOString().split('T')[0]}.sql`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setMessage({ type: 'success', text: 'Databáza bola úspešne exportovaná!' });
    } catch (error) {
      console.error('SQL export error:', error);
      setMessage({ type: 'error', text: 'SQL export zlyhal. Skúste to znova.' });
    } finally {
      setExporting(false);
    }
  };

  // Import from JSON
  const handleImportJSON = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setMessage(null);

    try {
      const text = await file.text();
      const data = JSON.parse(text);

      const token = getAuthToken();
      const response = await fetch(`${API_URL}/import/json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error('Import zlyhal');
      }

      const result = await response.json();
      setMessage({
        type: 'success',
        text: `Import úspešný! ${result.projects_imported || 0} projektov importovaných.`
      });

      // Refresh page after 2 seconds
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error) {
      console.error('Import error:', error);
      setMessage({ type: 'error', text: 'Import zlyhal. Skontrolujte formát súboru.' });
    } finally {
      setImporting(false);
      // Reset file input
      event.target.value = '';
    }
  };

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog-container settings-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>⚙️ Nastavenia</h2>
          <button className="dialog-close" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* Tabs */}
        <div className="settings-tabs">
          <button
            className={`settings-tab ${activeTab === 'general' ? 'active' : ''}`}
            onClick={() => setActiveTab('general')}
          >
            Všeobecné
          </button>
          <button
            className={`settings-tab ${activeTab === 'backup' ? 'active' : ''}`}
            onClick={() => setActiveTab('backup')}
          >
            Záloha a Obnova
          </button>
        </div>

        <div className="dialog-body settings-body">
          {/* General Tab */}
          {activeTab === 'general' && (
            <>
            <div className="settings-section">
              <h3>🎨 Vzhľad</h3>
              <p style={{ color: '#999', fontSize: '13px', lineHeight: 1.5 }}>
                Nové rozhranie je zjednotené — rovnaké tlačidlá, polia, karty a odstupy
                všade. Prepne sa okamžite a kedykoľvek sa dá vrátiť späť. Voľba platí
                len pre toto zariadenie, takže si technik v teréne môže nechať svoje.
              </p>

              <div className="tema-volba">
                <button
                  type="button"
                  className={`tema-moznost ${tema === 'klasicke' ? 'vybrana' : ''}`}
                  onClick={() => { setTema('klasicke'); nastavTemu('klasicke'); }}
                >
                  <span className="tema-nazov">Klasické</span>
                  <span className="tema-popis">Doterajší vzhľad appky</span>
                </button>
                <button
                  type="button"
                  className={`tema-moznost ${tema === 'nove' ? 'vybrana' : ''}`}
                  onClick={() => { setTema('nove'); nastavTemu('nove'); }}
                >
                  <span className="tema-nazov">Nové</span>
                  <span className="tema-popis">Zjednotené prvky, bez gradientov</span>
                </button>
              </div>
            </div>

            <div className="settings-section">
              <h3>📶 Signál na mape</h3>
              {!currentProject ? (
                <p style={{ color: '#999', fontSize: '14px' }}>
                  Najprv otvor projekt — hranice sa ukladajú ku konkrétnemu projektu.
                </p>
              ) : (
                <>
                  <p style={{ color: '#999', fontSize: '13px', lineHeight: 1.5 }}>
                    Podľa týchto hraníc sa farbí prstenec okolo zákazníckych splitterov.
                    Počíta sa výkon na <b>výstupe splittra</b> — teda to, čo dorazí
                    k zákazníkovi. Projekt: <b>{currentProject.name}</b>
                  </p>

                  <div className="form-group">
                    <label>Zelená — pripravené na zváranie (nad dBm)</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={prahZelena}
                      onChange={(e) => setPrahZelena(e.target.value)}
                      placeholder="-15"
                    />
                  </div>

                  <div className="form-group">
                    <label>Oranžová — slabý signál (nad dBm)</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={prahOranzova}
                      onChange={(e) => setPrahOranzova(e.target.value)}
                      placeholder="-25"
                    />
                    <small style={{ color: '#999', fontSize: '12px' }}>
                      Pod touto hodnotou je prstenec červený. Bežný prijímač GPON
                      zvládne asi do -27 dBm, tak nechaj rezervu na starnutie a ďalšie zvary.
                    </small>
                  </div>

                  <button className="btn-primary" onClick={ulozPrahy} disabled={ukladam}>
                    {ukladam ? 'Ukladám…' : 'Uložiť hranice'}
                  </button>

                  {message && (
                    <div className={`settings-message ${message.type}`} style={{ marginTop: '12px' }}>
                      {message.type === 'success' ? '✅' : '❌'} {message.text}
                    </div>
                  )}
                </>
              )}
            </div>
            </>
          )}

          {/* Backup & Restore Tab */}
          {activeTab === 'backup' && (
            <div className="settings-section">
              <h3>📦 Záloha a Obnova</h3>

              {/* Message */}
              {message && (
                <div className={`settings-message ${message.type}`}>
                  {message.type === 'success' ? '✅' : '❌'} {message.text}
                </div>
              )}

              {/* Export Section */}
              <div className="settings-group">
                <h4>Exportovať dáta</h4>
                <div className="settings-buttons">
                  <button
                    className="settings-button export"
                    onClick={handleExportAllProjects}
                    disabled={exporting}
                  >
                    <span className="button-icon">💾</span>
                    <div className="button-content">
                      <div className="button-title">Exportovať všetky projekty (JSON)</div>
                      <div className="button-description">
                        Stiahnuť všetky projekty, stĺpiky, káble a konfigurácie
                      </div>
                    </div>
                  </button>

                  <button
                    className="settings-button export"
                    onClick={handleExportCurrentProject}
                    disabled={exporting || !currentProject}
                  >
                    <span className="button-icon">📊</span>
                    <div className="button-content">
                      <div className="button-title">Exportovať aktuálny projekt</div>
                      <div className="button-description">
                        {currentProject ? `Exportovať "${currentProject.name}"` : 'Nie je vybraný projekt'}
                      </div>
                    </div>
                  </button>

                  <button
                    className="settings-button export"
                    onClick={handleExportSQL}
                    disabled={exporting}
                  >
                    <span className="button-icon">🗄️</span>
                    <div className="button-content">
                      <div className="button-title">Exportovať databázu (SQL)</div>
                      <div className="button-description">
                        Stiahnuť kompletný dump databázy
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              {/* Import Section */}
              <div className="settings-group">
                <h4>Importovať dáta</h4>
                <div className="settings-buttons">
                  <label className="settings-button import">
                    <input
                      type="file"
                      accept=".json"
                      onChange={handleImportJSON}
                      disabled={importing}
                      style={{ display: 'none' }}
                    />
                    <span className="button-icon">📥</span>
                    <div className="button-content">
                      <div className="button-title">Importovať z JSON</div>
                      <div className="button-description">
                        Obnoviť dáta zo záložného súboru
                      </div>
                    </div>
                  </label>
                </div>
                <div className="settings-warning">
                  ⚠️ Upozornenie: Import zlúči dáta s existujúcou databázou. Môžu vzniknúť duplicitné projekty.
                </div>
              </div>

              {/* Info */}
              <div className="settings-info">
                <div className="info-item">
                  <span className="info-label">Aktuálny projekt:</span>
                  <span className="info-value">{currentProject?.name || 'Žiadny'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Posledná záloha:</span>
                  <span className="info-value">
                    {localStorage.getItem('lastBackup') || 'Nikdy'}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="dialog-footer">
          <button className="dialog-button secondary" onClick={onClose}>
            Zavrieť
          </button>
        </div>
      </div>
    </div>
  );
};

